import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import Avatar from '../common/Avatar';
import LoadingSpinner from '../ui/LoadingSpinner';
import ReferralStats from '../user/ReferralStats';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';

export default function UserProfile() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form állapotok
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    phone: '',
    referralCode: '',
    notificationPreferences: {
      email: true,
      push: true
    }
  });

  useEffect(() => {
    async function fetchUserProfile() {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        // Admin státusz ellenőrzése
        const systemAdminsDoc = await getDoc(doc(db, 'systemSettings', 'system_admins'));
        if (systemAdminsDoc.exists()) {
          const { systemAdmins = [] } = systemAdminsDoc.data();
          setIsAdmin(systemAdmins.includes(currentUser.uid));
        }
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setFormData({
            displayName: userData.displayName || '',
            email: currentUser.email,
            phone: userData.phone || '',
            referralCode: userData.referralCode || currentUser.uid.slice(0, 8),
            notificationPreferences: {
              email: userData.notificationPreferences?.email || true,
              push: userData.notificationPreferences?.push || true
            }
          });
        }
      } catch (err) {
        toast.error('Hiba történt a profil betöltésekor');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchUserProfile();
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Az ajánlói kódot nem frissítjük, mivel az nem módosítható
      await updateDoc(doc(db, 'users', currentUser.uid), {
        displayName: formData.displayName,
        phone: formData.phone,
        notificationPreferences: formData.notificationPreferences,
        updatedAt: new Date()
      });
      toast.success('Profil sikeresen frissítve!');
    } catch (err) {
      toast.error('Hiba történt a mentés során');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Profil beállítások
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Bal oldali profil kártya */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 h-32"></div>
            <div className="px-6 py-4 -mt-16">
              <div className="flex justify-center">
                <Avatar user={currentUser} size="xl" className="border-4 border-white" />
              </div>
              <div className="text-center mt-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {formData.displayName || 'Felhasználó'}
                </h3>
                <p className="text-gray-600">{formData.email}</p>
                {formData.phone && (
                  <p className="text-gray-600 mt-1">
                    <Icons.Phone className="inline-block h-4 w-4 mr-1" />
                    {formData.phone}
                  </p>
                )}
              </div>
              
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                  Ajánlói kód
                </h4>
                <div className="mt-2 flex items-center justify-between bg-gray-50 rounded-md p-2">
                  <code className="text-sm font-mono text-indigo-600">
                    {formData.referralCode}
                  </code>
                  <button 
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(formData.referralCode);
                      toast.success('Ajánlói kód másolva!');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icons.Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {isAdmin && (
                <div className="mt-6 border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Admin funkciók
                  </h4>
                  <div className="mt-2">
                    <a 
                      href="/admin/dashboard" 
                      className="flex items-center text-indigo-600 hover:text-indigo-800"
                    >
                      <Icons.LayoutDashboard className="h-4 w-4 mr-2" />
                      Admin felület
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <ReferralStats className="mt-6" />
        </div>
        
        {/* Jobb oldali űrlap */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-6">
              <h3 className="text-lg font-medium text-gray-900 mb-6">
                Személyes adatok
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">
                      Teljes név
                    </label>
                    <input
                      type="text"
                      name="displayName"
                      id="displayName"
                      value={formData.displayName}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email cím
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      disabled
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                      Telefonszám
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      id="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700">
                      Ajánlói kód
                    </label>
                    <input
                      type="text"
                      name="referralCode"
                      id="referralCode"
                      value={formData.referralCode}
                      disabled
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 bg-gray-50 text-gray-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Értesítési beállítások
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <input
                        id="email-notifications"
                        name="notificationPreferences.email"
                        type="checkbox"
                        checked={formData.notificationPreferences.email}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            notificationPreferences: {
                              ...prev.notificationPreferences,
                              email: e.target.checked
                            }
                          }));
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="email-notifications" className="ml-3 text-sm text-gray-700">
                        Email értesítések
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        id="push-notifications"
                        name="notificationPreferences.push"
                        type="checkbox"
                        checked={formData.notificationPreferences.push}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            notificationPreferences: {
                              ...prev.notificationPreferences,
                              push: e.target.checked
                            }
                          }));
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor="push-notifications" className="ml-3 text-sm text-gray-700">
                        Push értesítések
                      </label>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Icons.Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                        Mentés...
                      </>
                    ) : (
                      'Mentés'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 